import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import { FiArrowLeft, FiUser, FiBarChart2, FiTrendingUp, FiClock, FiGlobe, FiSearch, FiSend, FiMail, FiFileText, FiMessageCircle } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { HolderActivityView, HolderDetailsResult } from '../../../model/HolderDetailsResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import SendWelcomeEmailModal from './SendWelcomeEmailModal';

// ShareRegistryProvider enum order (investi.Core/Model/ShareRegistryProvider.cs)
const providers = ['None', 'Automic', 'Xcend', 'Computershare', 'MUFG', 'Advanced', 'Boardroom'];
const fmt = (n: number | undefined | null, decimals = 0) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const fmtDate = (d?: string) => d ? moment(d).format('DD MMM YYYY') : '';
const compact = (v: number) => v >= 1e9 ? +(v / 1e9).toFixed(1) + 'B' : v >= 1e6 ? +(v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? +(v / 1e3).toFixed(1) + 'K' : String(v);
const holdingColors = ['#0b1739', '#64748b', '#9a6b00'];
const isDocument = (a: HolderActivityView) => a.type === 'Page view' && /\.pdf(\?|$)/i.test(a.description);

// activity filter → predicate; counts feed the engagement tiles too
const activityFilters: { key: string; label: string; match: (a: HolderActivityView) => boolean }[] = [
    { key: 'all', label: 'Everything', match: () => true },
    { key: 'visits', label: 'Website Visits', match: a => a.type === 'Page view' && !isDocument(a) },
    { key: 'emails', label: 'Emails', match: a => a.type === 'Email Campaign' },
    { key: 'documents', label: 'Documents', match: isDocument },
    { key: 'questions', label: 'Questions', match: a => a.type === 'Comment' },
];
const activityIcon = (a: HolderActivityView) => a.type === 'Email Campaign' ? <FiMail /> : a.type === 'Comment' ? <FiMessageCircle /> : isDocument(a) ? <FiFileText /> : <FiTrendingUp />;
const activityTitle = (a: HolderActivityView) => {
    const text = /^https?:\/\//.test(a.description) ? a.description.replace(/^https?:\/\/[^/]+/, '') : a.description;
    if (a.type === 'Email Campaign') return `${a.actions?.includes('Opened') ? 'Opened' : 'Received'} “${text}”`;
    if (a.type === 'Comment') return `Asked “${text}”`;
    return `${isDocument(a) ? 'Downloaded' : 'Visited'} ${text}`;
};
const activitySub = (a: HolderActivityView) => [
    moment(a.date).format('h:mm a'),
    a.meta,
    a.count && a.count > 1 ? `${a.count} visits` : null,
    a.type === 'Email Campaign' && a.actions?.length ? a.actions.join(' · ').toLowerCase() : null,
].filter(Boolean).join(' · ');
const dayLabel = (d: string) => {
    const m = moment(d);
    if (m.isSame(moment(), 'day')) return 'Today';
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return `Yesterday · ${m.format('dddd D MMMM')}`;
    return m.format(m.isSame(moment(), 'year') ? 'D MMMM' : 'D MMMM YYYY');
};

interface Props { holderId?: string } // numeric id, or "web" with ?email= for a sign-up who is not on the register

const HolderDetail: React.FC<Props> = ({ holderId }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const history = useHistory();
    const email = new URLSearchParams(useLocation().search).get('email') || undefined;
    const [result, setResult] = useState<HolderDetailsResult | null>(null);
    const [error, setError] = useState('');
    const [showAll, setShowAll] = useState(false);
    const [filter, setFilter] = useState('all');
    const [showWelcome, setShowWelcome] = useState(false);

    useAsyncEffect(async () => {
        if (!stock) return;
        const params = email ? { email } : { holderId };
        const [err, data] = await to(api().getHolderDetails(stock.ticker, stock.exchange, params));
        if (err || !data.success) setError(err?.message || data?.message || 'Failed to fetch holder details');
        else setResult(data);
    }, [stock?.id, holderId, email]);

    const provider = providers[stock?.shareRegistryProvider ?? 0] ?? 'registry';
    const currency = stock?.exchange === 'ASX' ? 'A$' : '$';
    const ticker = stock?.ticker || '';
    const holder = result?.holder;
    const summary = result?.summary;
    const movements = result?.movements || [];
    const activity = result?.activity || [];

    const codes = useMemo(() => {
        const set = Array.from(new Set(movements.map(m => m.securityCode || ticker)));
        return set.length ? set : [ticker];
    }, [result]);

    // the chart runs from the first investment; movements arrive oldest-first
    const chartOption = useMemo(() => {
        if (!result || !summary) return null;
        const holdingSeries = codes.map(code => {
            const ms = movements.filter(m => (m.securityCode || ticker) === code);
            const data: (string | number)[][] = ms.map(m => [m.date, m.closingBalance]);
            if (data.length) data.push([moment().format('YYYY-MM-DD'), code === ticker ? summary.currentBalance : ms[ms.length - 1].closingBalance]);
            const color = holdingColors[codes.indexOf(code) % holdingColors.length];
            return { name: `${code} holding`, type: 'line', step: 'end', symbol: 'none', data, yAxisIndex: 0, lineStyle: { color, width: 2 }, itemStyle: { color } };
        });
        const from = holder?.firstInvestmentDate || movements[0]?.date;
        const price = (result.priceSeries || []).filter(p => !from || moment(p.date).isSameOrAfter(moment(from).subtract(2, 'weeks'))).map(p => [p.date, p.close]);
        const maxPrice = Math.max(0, ...price.map(p => p[1] as number));
        const priceLabel = (v: number) => `${currency}${v.toFixed(maxPrice < 0.1 ? 3 : 2)}`;
        return {
            grid: { left: 56, right: 64, top: 12, bottom: 28 },
            tooltip: { trigger: 'axis', backgroundColor: '#0b1739', borderWidth: 0, textStyle: { color: '#fff', fontSize: 12 } },
            xAxis: { type: 'time', axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#64748b', fontSize: 12, formatter: (d: string) => moment(d).format('MMM') } },
            yAxis: [
                { type: 'value', splitNumber: 5, splitLine: { lineStyle: { color: '#e5e9f0', type: 'dashed' } }, axisLabel: { color: '#64748b', fontSize: 12, formatter: (v: number) => compact(v) } },
                { type: 'value', alignTicks: true, position: 'right', splitLine: { show: false }, axisLabel: { color: '#0093ab', fontSize: 12, formatter: priceLabel } },
            ],
            series: [...holdingSeries, { name: 'Share price', type: 'line', symbol: 'none', smooth: true, data: price, yAxisIndex: 1, lineStyle: { color: '#00B5D1', width: 2 }, itemStyle: { color: '#00B5D1' } }],
        };
    }, [result]);

    const back = <button type="button" className="link back" onClick={() => history.push('/share-registry')}><FiArrowLeft /> Back to the register</button>;
    if (error) return <div className="holder-detail">{back}<p className="error">{error}</p></div>;
    if (!result || !holder) return <div className="holder-detail">{back}<p className="loading">{email ? 'Loading details…' : `Loading holder details from ${provider}… (first load can take a minute while reports generate)`}</p></div>;

    const subscription = result.unsubscribed ? <span className="pill bad">Unsubscribed</span>
        : result.subscribedOn ? <span className="pill ok">Subscribed on {fmtDate(result.subscribedOn)}</span>
        : holder.email && holder.holderId ? <button type="button" className="btn-primary" onClick={() => setShowWelcome(true)}><FiSend /> Send Welcome Email</button>
        : null;
    const welcomeModal = showWelcome && <SendWelcomeEmailModal combinedTicker={`${ticker}:${stock?.exchange}`}
        holder={{ holderId: holder.holderId, name: holder.name, email: holder.email, balance: summary?.currentBalance ?? 0 }}
        onClose={() => setShowWelcome(false)}
        onSent={subscribed => { setShowWelcome(false); if (subscribed) setResult({ ...result, subscribedOn: new Date().toISOString() }); }} />;

    const counts = Object.fromEntries(activityFilters.map(f => [f.key, activity.filter(f.match).length]));
    const opened = activity.filter(a => a.type === 'Email Campaign' && a.actions?.includes('Opened')).length;
    const visits = activity.filter(activityFilters[1].match).reduce((s, a) => s + (a.count || 1), 0);
    const lastVisit = activity.find(activityFilters[1].match)?.date;
    const shown = activity.filter(activityFilters.find(f => f.key === filter)!.match);
    const days = Array.from(new Set(shown.map(a => moment(a.date).format('YYYY-MM-DD'))));
    const nominee = /nominee/i.test(`${holder.name} ${holder.holderType}`);

    const activitySection = <div className="card section activity">
        <div className="section-title">
            <span className="icon-tile"><FiGlobe /></span>
            <div><h2>Website and Email Activity</h2><p>{activity.length ? 'Everything this holder has done on your website and in your emails — newest first' : `What this holder has done on ${(stock?.websiteUrl || '').replace(/^https?:\/\//, '').replace(/\/$/, '') || 'your website'}`}</p></div>
        </div>
        {activity.length === 0 && <div className="empty">
            <span className="icon-tile"><FiSearch /></span>
            <h3>No activity found</h3>
            <p>This holder has not signed up on the Website Hub, so we can't match them to any emails or website visits.{nominee && ' Nominee companies like this one usually never do — the real owner sits behind them.'}</p>
        </div>}
        {activity.length > 0 && <>
            <div className="engagement">
                <div className="k"><span className="icon-tile"><FiMail /></span><div><div className="v">{counts.emails}</div><div className="l">emails received</div><div className="d">{opened} opened ({counts.emails ? Math.round(opened * 100 / counts.emails) : 0}%)</div></div></div>
                <div className="k"><span className="icon-tile"><FiTrendingUp /></span><div><div className="v">{visits}</div><div className="l">website visits</div><div className="d">{lastVisit ? `last seen ${moment(lastVisit).fromNow()}` : 'none yet'}</div></div></div>
                <div className="k"><span className="icon-tile"><FiFileText /></span><div><div className="v">{counts.documents}</div><div className="l">documents opened</div><div className="d">PDF downloads</div></div></div>
                <div className="k"><span className="icon-tile"><FiMessageCircle /></span><div><div className="v">{counts.questions}</div><div className="l">questions asked</div><div className="d">announcement comments</div></div></div>
            </div>
            <div className="filters">
                {activityFilters.map(f => <button key={f.key} type="button" className={filter === f.key ? 'active' : ''} onClick={() => setFilter(f.key)}>{f.label} <span className="n">{counts[f.key]}</span></button>)}
            </div>
            <div className="list">
                {days.map(day => <div key={day} className="day">
                    <div className="day-label">{dayLabel(day)}</div>
                    {shown.filter(a => moment(a.date).format('YYYY-MM-DD') === day).map((a, i) => <div key={i} className="item">
                        <span className="icon-tile">{activityIcon(a)}</span>
                        <div><div className="t">{activityTitle(a)}</div><div className="s">{activitySub(a)}</div></div>
                    </div>)}
                </div>)}
                {shown.length === 0 && <p className="none">Nothing of this type yet</p>}
            </div>
            <p className="footnote">{activity.length} events · everything since {fmtDate(activity[activity.length - 1].date)}</p>
        </>}
    </div>;

    if (result.notAShareholder) return <div className="holder-detail">
        {back}
        <div className="page-header">
            <div>
                <h1>{holder.name || holder.email}</h1>
                <div className="badges"><span className="pill">Not a shareholder</span><span className="meta">{holder.email}</span>{subscription}</div>
            </div>
        </div>
        {activitySection}
    </div>;

    const pct = result.issuedCapital ? summary!.currentBalance * 100 / result.issuedCapital : null;
    const recent = [...movements].reverse();
    const rows = showAll ? recent : recent.slice(0, 5);
    const multiSecurity = new Set(movements.map(m => m.securityCode || ticker)).size > 1;

    return <div className="holder-detail">
        {welcomeModal}
        {back}
        <div className="page-header">
            <div>
                <h1>{holder.name}</h1>
                <div className="badges">
                {result.isNew && <span className="pill ok">New holder</span>}
                {result.isTop20 && <span className="pill top">Top 20 holder</span>}
                {pct !== null && pct >= 5 && <span className="pill warn">Substantial holder (5%+)</span>}
                <span className="meta">Holder no. {holder.holderNumber}{holder.holderType && ` · ${holder.holderType.toLowerCase()}`}</span>
            </div>
            </div>
        </div>

        <div className="top">
            <div className="card section details">
                <div className="section-title"><span className="icon-tile"><FiUser /></span><div><h2>Holder Details</h2><p>As supplied by {provider}</p></div></div>
                <div className="kv"><span>Holder number</span><span>{holder.holderNumber}</span></div>
                <div className="kv"><span>Holder ID</span><span>{holder.holderId}</span></div>
                <div className="kv"><span>Type</span><span>{holder.holderType}</span></div>
                <div className="kv"><span>Address</span><span className="address">{(holder.address || '').split(',').map(s => s.trim()).filter(Boolean).join('\n')}</span></div>
                <div className="kv"><span>Email</span><span>{holder.email ? <>{holder.email}<div className="sub-action">{subscription}</div></> : <span className="muted">No email on file</span>}</span></div>
                <div className="kv"><span>Phone</span><span>{holder.phone || <span className="muted">—</span>}</span></div>
                <div className="kv"><span>First invested</span><span>{fmtDate(holder.firstInvestmentDate) || <span className="muted">—</span>}</span></div>
            </div>
            <div className="card section position">
                <div className="section-title"><span className="icon-tile"><FiBarChart2 /></span><div><h2>Position</h2><p>At today's price</p></div></div>
                <div className="big">{fmt(summary!.currentBalance)}</div>
                <div className="big-sub">shares held{pct !== null && ` · ${pct > 0 && pct < 0.01 ? '<0.01' : pct.toFixed(2)}% of issued capital`}</div>
                <div className="kv"><span>Current price</span><span>{currency}{fmt(summary!.currentPrice, 3)}</span></div>
                <div className="kv"><span>Current value</span><span>{currency}{fmt(summary!.currentValue, 2)}</span></div>
                <div className="kv"><span>Change this quarter</span><span>{result.quarterChangePts == null ? <span className="muted">—</span>
                    : <span className={result.quarterChangePts > 0 ? 'up' : result.quarterChangePts < 0 ? 'down' : ''}>{result.quarterChangePts > 0 ? '+' : ''}{result.quarterChangePts.toFixed(1)} pts</span>}</span></div>
                <div className="kv"><span>Last movement</span><span>{recent[0] ? fmtDate(recent[0].date) : <span className="muted">—</span>}</span></div>
            </div>
        </div>

        <div className="card section chart">
            <div className="section-title">
                <span className="icon-tile"><FiTrendingUp /></span>
                <div><h2>Holding and Share Price</h2><p>Since this holder first invested</p></div>
                <div className="legend">{codes.map((c, i) => <span key={c}><i style={{ background: holdingColors[i % holdingColors.length] }} /> {c} holding</span>)}<span><i className="cyan" /> Share price</span></div>
            </div>
            {chartOption && <ReactECharts option={chartOption} style={{ height: 260 }} notMerge={true} />}
        </div>

        <div className="card section movements">
            <div className="section-title"><span className="icon-tile"><FiClock /></span><div><h2>Movement History</h2><p>Every change to this holding since the float</p></div></div>
            <div className="table">
                <div className={`row head${multiSecurity ? ' security' : ''}`}>
                    <span>Date</span>{multiSecurity && <span>Security</span>}<span>Description</span><span>Broker</span><span className="num">Movement</span><span className="num">Price</span><span className="num">Closing balance</span>
                </div>
                {rows.map((m, i) => <div className={`row${multiSecurity ? ' security' : ''}`} key={i}>
                    <span className="date">{fmtDate(m.date)}</span>
                    {multiSecurity && <span>{m.securityCode || ticker}</span>}
                    <span>{m.description}{m.movementType && !(m.description || '').toLowerCase().includes(m.movementType.toLowerCase()) ? ` (${m.movementType})` : ''}</span>
                    <span>{m.broker}</span>
                    <span className={`num ${m.movement >= 0 ? 'up' : 'down'}`}>{m.movement >= 0 ? '+' : '−'}{fmt(Math.abs(m.movement))}</span>
                    <span className="num">{m.price != null ? `${currency}${fmt(m.price, 3)}${m.priceEstimated ? '*' : ''}` : m.priceMissing ? '†' : ''}</span>
                    <span className="num">{fmt(m.closingBalance)}</span>
                </div>)}
                {movements.length === 0 && <p className="none">No movements found</p>}
            </div>
            <div className="foot">
                <div className="footnotes">
                    {movements.some(m => m.priceEstimated) && <p>* the registry does not give a price for this movement — estimated from the closing price on the trade date</p>}
                    {movements.some(m => m.priceMissing) && <p>† no closing price found near the trade date</p>}
                </div>
                {movements.length > 5 && <div className="more">
                    <span>Showing {rows.length} of {movements.length}</span>
                    <button type="button" className="btn-outline" onClick={() => setShowAll(!showAll)}>{showAll ? 'Show Fewer' : 'Show All'}</button>
                </div>}
            </div>
        </div>

        {activitySection}
    </div>;
};

export default HolderDetail;
